import { useEffect, useState } from "react";
import { Shield, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import ChatLayout from "@/components/ChatLayout";
import AdminInboxSidebar from "@/components/AdminInboxSidebar";
import { adminLogin, fetchAdminInbox } from "@/lib/chatApi";
import { AVATARS } from "@/lib/avatars";

const KEY = "ruangchat_admin_session";

function AdminLoginForm({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return toast.error("Isi username dan password admin");
    setLoading(true);
    try {
      const res = await adminLogin(username.trim(), password);
      onLogin({ username: res.username, avatar: AVATARS[0], isAdmin: true, token: res.token });
    } catch {
      setFailed(true);
      toast.error("Username atau password admin salah");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full px-4 py-3 rounded-xl bg-slate-800/70 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm";
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm bg-slate-900/80 border border-slate-800 rounded-3xl p-8 space-y-4">
        <div className="w-14 h-14 rounded-2xl bg-amber-500 flex items-center justify-center">
          <Shield className="w-7 h-7 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-white font-heading">Dashboard Admin</h1>
          <p className="text-sm text-slate-400 mt-1">Masuk untuk membalas chat di room yang Anda tangani.</p>
        </div>
        <input data-testid="admin-login-username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username admin" className={field} />
        <input data-testid="admin-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={field} />
        <button data-testid="admin-login-submit" disabled={loading} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-semibold text-sm transition-colors disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          Masuk Dashboard
        </button>
        {failed && (
          <p data-testid="admin-login-hint" className="text-xs text-amber-300/80 text-center">
            Username atau password admin salah. Akun admin di versi live terpisah dari preview.
          </p>
        )}
      </form>
    </div>
  );
}

export default function AdminDashboard() {
  const [session, setSession] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; }
  });
  const [inbox, setInbox] = useState(null);

  const logout = () => {
    localStorage.removeItem(KEY);
    setSession(null);
    setInbox(null);
  };

  useEffect(() => {
    if (!session) return;
    fetchAdminInbox(session.token)
      .then(setInbox)
      .catch((e) => {
        if (e?.response?.status === 401) {
          toast.error("Sesi admin berakhir, silakan login lagi");
          logout();
        } else toast.error("Gagal memuat inbox admin");
      });
  }, [session]);

  if (!session) {
    return <AdminLoginForm onLogin={(u) => { localStorage.setItem(KEY, JSON.stringify(u)); setSession(u); }} />;
  }
  if (!inbox) {
    return <div className="h-screen flex items-center justify-center bg-slate-950"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
  }

  const initialLast = Object.fromEntries(inbox.rooms.filter((r) => r.last_message).map((r) => [r.id, r.last_message]));
  const latestRoomId = Object.entries(initialLast).sort((a, b) => (a[1].created_at < b[1].created_at ? 1 : -1))[0]?.[0] || null;
  return (
    <div data-testid="admin-dashboard">
      <ChatLayout
        user={{ ...session, isModerator: inbox.is_moderator }}
        onLogout={logout}
        chatOptions={{ roomIds: inbox.rooms.map((r) => r.id), initialLast, desktopNotify: true, initialRoom: latestRoomId }}
        CustomSidebar={AdminInboxSidebar}
      />
    </div>
  );
}
